'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Bell,
  BellOff,
  CalendarClock,
  ChevronDown,
  ClipboardList,
  Loader2,
  MessageSquarePlus,
  Search,
  X,
} from 'lucide-react';
import { isOidcConfigured } from '../../lib/auth';
import type { RequestThread, RequestUpdate, SubmissionRow, ThreadComment } from '../../types/submissions';
import { usePushNotifications } from '../../hooks/usePushNotifications';

// ── Constants ─────────────────────────────────────────────────────────────────

const FORM_TYPE_LABELS: Record<string, string> = {
  collateral: 'Collateral',
  'trade-show': 'Trade Show',
  'quick-note': 'Quick Note',
};

const FORM_TYPE_COLORS: Record<string, string> = {
  collateral: 'bg-blue-50 text-blue-600',
  'trade-show': 'bg-purple-50 text-purple-600',
  'quick-note': 'bg-green-50 text-green-600',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusStyle(status: string | null | undefined): string {
  if (!status) return 'bg-gray-100 text-gray-500';
  const s = status.toLowerCase();
  if (s === 'complete' || s === 'completed' || s === 'done') return 'bg-green-50 text-green-700';
  if (s.includes('progress') || s.includes('doing') || s.includes('active')) return 'bg-blue-50 text-blue-700';
  if (s.includes('review') || s.includes('approval')) return 'bg-yellow-50 text-yellow-700';
  return 'bg-gray-100 text-gray-500';
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Asana due dates are bare YYYY-MM-DD — parsing them as UTC would shift the day. */
function formatDueDate(dueOn: string | null | undefined) {
  if (!dueOn) return '';
  const m = dueOn.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dueOn;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatWhen(iso: string) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function updateLabel(u: RequestUpdate): string {
  if (u.kind === 'status') {
    return u.fromValue ? `Status: ${u.fromValue} → ${u.toValue ?? 'Open'}` : `Status: ${u.toValue ?? 'Open'}`;
  }
  if (!u.toValue) return 'Due date removed';
  return u.fromValue
    ? `Due date: ${formatDueDate(u.fromValue)} → ${formatDueDate(u.toValue)}`
    : `Due date set to ${formatDueDate(u.toValue)}`;
}

// ── Timeline ──────────────────────────────────────────────────────────────────

type TimelineEntry =
  | { at: string; type: 'comment'; comment: ThreadComment }
  | { at: string; type: 'event'; update: RequestUpdate };

function buildTimeline(thread: RequestThread): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...thread.comments.map((c) => ({ at: c.createdAt, type: 'comment' as const, comment: c })),
    ...thread.activity
      .filter((u) => u.kind !== 'comment')
      .map((u) => ({ at: u.createdAt, type: 'event' as const, update: u })),
  ];
  return entries.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

function Timeline({ thread }: { thread: RequestThread }) {
  const entries = buildTimeline(thread);

  if (entries.length === 0) {
    return (
      <p className="text-[11px] text-muted">
        No updates yet. Anything the marketing team changes or comments shows up here.
      </p>
    );
  }

  return (
    <ol className="space-y-2">
      {entries.map((e) =>
        e.type === 'event' ? (
          <li key={`u-${e.update.id}`} className="flex items-center gap-2 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/40 flex-shrink-0" aria-hidden />
            <span className="text-[11px] text-muted">{updateLabel(e.update)}</span>
            <span className="text-[10px] text-subtle ml-auto flex-shrink-0">{formatWhen(e.at)}</span>
          </li>
        ) : (
          <li
            key={`c-${e.comment.gid}`}
            className={`rounded-md border px-2.5 py-2 ${
              e.comment.fromRequester
                ? 'border-border bg-surface-sunken'
                : 'border-border bg-surface border-l-2 border-l-accent'
            }`}
          >
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span
                className={`text-[11px] font-semibold ${
                  e.comment.fromRequester ? 'text-muted' : 'text-primary'
                }`}
              >
                {e.comment.author}
              </span>
              <span className="text-[10px] text-subtle flex-shrink-0">{formatWhen(e.at)}</span>
            </div>
            <p className="text-[12px] text-foreground leading-relaxed whitespace-pre-wrap break-words">
              {e.comment.text}
            </p>
          </li>
        ),
      )}
    </ol>
  );
}

// ── Comment box ───────────────────────────────────────────────────────────────

function CommentBox({
  taskGid,
  token,
  onPosted,
}: {
  taskGid: string;
  token: string;
  onPosted: () => void;
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setErr('');
    try {
      const r = await fetch('/api/asana-comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ taskGid, comment: text.trim() }),
      });
      if (!r.ok) {
        let msg = `Error ${r.status}`;
        try { msg = (await r.json() as { error?: string }).error ?? msg; } catch { /* non-JSON body */ }
        throw new Error(msg);
      }
      setText('');
      onPosted();
    } catch (e) {
      setErr((e as Error).message || 'Failed to send comment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Reply to the marketing team…"
        rows={3}
        className="w-full text-[12px] text-foreground placeholder:text-subtle border border-border rounded-md px-2.5 py-2
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-interactive focus:border-primary/50 resize-none transition"
      />
      {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-muted">Posted to the Asana task and emailed to its owner.</span>
        <button
          onClick={submit}
          disabled={saving || !text.trim()}
          className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-md bg-primary text-white
                     hover:bg-primary-light disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          {saving ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <MessageSquarePlus className="w-3 h-3" />
          )}
          {saving ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── Request row ───────────────────────────────────────────────────────────────

function RequestRow({
  row,
  token,
  onOpened,
}: {
  row: SubmissionRow;
  token: string;
  onOpened: (taskGid: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState<RequestThread | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [threadError, setThreadError] = useState('');

  const hasDetails = (row.details?.length ?? 0) > 0;
  const unread = row.unreadCount ?? 0;
  const latest = row.updates?.[0];

  const loadThread = useCallback(async () => {
    if (!row.asanaTaskId || !token) return;
    setLoadingThread(true);
    setThreadError('');
    try {
      const r = await fetch('/api/request-thread', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ taskGid: row.asanaTaskId }),
      });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      setThread((await r.json()) as RequestThread);
      onOpened(row.asanaTaskId);
    } catch (e) {
      setThreadError((e as Error).message || 'Could not load the conversation.');
    } finally {
      setLoadingThread(false);
    }
  }, [row.asanaTaskId, token, onOpened]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !thread && !loadingThread) void loadThread();
  };

  return (
    <li className="rounded-lg border border-border bg-surface-sunken/60 overflow-hidden">
      {/* ── Summary (always visible) ─────────────────────────────────── */}
      <button
        onClick={toggle}
        aria-expanded={open}
        className="w-full text-left px-3 py-2.5 transition-colors hover:bg-surface-sunken cursor-pointer"
      >
        {/* Title + date */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-semibold text-foreground leading-snug flex-1 min-w-0 truncate">
            {unread > 0 && (
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-accent mr-1.5 align-middle"
                aria-hidden
              />
            )}
            {row.requestTitle || '—'}
          </p>
          <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
            <span className="text-[10px] text-muted whitespace-nowrap">
              {formatDate(row.timestamp)}
            </span>
            <ChevronDown
              className={`w-3 h-3 text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {/* Badges row */}
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <span
            className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
              FORM_TYPE_COLORS[row.formType] ?? 'bg-gray-100 text-gray-600'
            }`}
          >
            {FORM_TYPE_LABELS[row.formType] ?? row.formType}
          </span>

          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusStyle(row.asanaStatus)}`}>
            {row.asanaStatus || 'Open'}
          </span>

          {row.dueOn && (
            <span className="flex items-center gap-1 text-[10px] text-muted">
              <CalendarClock className="w-3 h-3" />
              Due {formatDueDate(row.dueOn)}
            </span>
          )}

          {unread > 0 && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-accent/20 text-foreground">
              {unread} new
            </span>
          )}

          {row.department && <span className="text-[10px] text-muted">{row.department}</span>}
        </div>

        {/* Latest update line — the reason a requester opens this at all */}
        {latest && !open && (
          <p className="text-[10px] text-muted mt-1.5 truncate">
            {latest.kind === 'comment'
              ? `${latest.actor ?? 'Marketing'}: ${latest.body ?? ''}`
              : updateLabel(latest)}
            <span className="text-subtle"> · {formatWhen(latest.createdAt)}</span>
          </p>
        )}
      </button>

      {/* ── Detail panel (expandable) ────────────────────────────────── */}
      {open && (
        <div className="border-t border-border px-3 py-3 bg-surface">
          {hasDetails && (
            <dl className="space-y-2">
              {row.details!.map(({ label, value }) => (
                <div key={label}>
                  <dt className="text-[10px] font-semibold text-muted uppercase tracking-wide mb-0.5">
                    {label}
                  </dt>
                  <dd className="text-[12px] text-foreground leading-relaxed whitespace-pre-wrap break-words">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {row.asanaTaskId && (
            <div className={hasDetails ? 'mt-3 pt-3 border-t border-border' : ''}>
              <p className="text-[10px] font-semibold text-muted uppercase tracking-wide mb-1.5">
                Updates &amp; Comments
              </p>

              {loadingThread && (
                <div className="flex items-center gap-2 py-3 text-muted">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-[11px]">Loading conversation…</span>
                </div>
              )}

              {!loadingThread && threadError && (
                <div className="flex items-center gap-2 py-1">
                  <p className="text-[11px] text-red-600">{threadError}</p>
                  <button
                    onClick={() => void loadThread()}
                    className="text-[11px] font-medium text-primary hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loadingThread && !threadError && thread && <Timeline thread={thread} />}

              <CommentBox
                taskGid={row.asanaTaskId}
                token={token}
                onPosted={() => void loadThread()}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const CONFETTI_PARTICLES = [
  { tx: '0px',   ty: '-26px', color: '#FFAA00', delay: '0ms',  size: 5, round: true  },
  { tx: '18px',  ty: '-18px', color: '#FF6B35', delay: '30ms', size: 4, round: false },
  { tx: '26px',  ty: '0px',   color: '#FFD700', delay: '0ms',  size: 5, round: true  },
  { tx: '18px',  ty: '18px',  color: '#FF4466', delay: '60ms', size: 4, round: false },
  { tx: '0px',   ty: '26px',  color: '#44BBFF', delay: '30ms', size: 5, round: true  },
  { tx: '-18px', ty: '18px',  color: '#FF6B35', delay: '0ms',  size: 4, round: false },
  { tx: '-26px', ty: '0px',   color: '#FFD700', delay: '60ms', size: 5, round: true  },
  { tx: '-18px', ty: '-18px', color: '#FFAA00', delay: '30ms', size: 4, round: false },
  { tx: '9px',   ty: '-32px', color: '#FF4466', delay: '15ms', size: 3, round: true  },
  { tx: '-9px',  ty: '-32px', color: '#44BBFF', delay: '45ms', size: 3, round: false },
  { tx: '32px',  ty: '-9px',  color: '#FFAA00', delay: '20ms', size: 3, round: true  },
  { tx: '-32px', ty: '-9px',  color: '#FF6B35', delay: '50ms', size: 3, round: false },
];

function NotificationToggle({ token }: { token: string }) {
  const { state, subscribe, unsubscribe } = usePushNotifications(token);
  const [celebrating, setCelebrating] = useState(false);
  const prevStateRef = useRef<string>('');

  useEffect(() => {
    if (prevStateRef.current !== 'subscribed' && state === 'subscribed') {
      // Deferred so the celebration doesn't setState synchronously inside the effect
      const start = setTimeout(() => setCelebrating(true), 0);
      const stop = setTimeout(() => setCelebrating(false), 2400);
      prevStateRef.current = state;
      return () => { clearTimeout(start); clearTimeout(stop); };
    }
    prevStateRef.current = state;
  }, [state]);

  if (state === 'unsupported') return null;

  const isOn = state === 'subscribed';
  const isLoading = state === 'loading';
  const isBlocked = state === 'blocked';

  const tooltip = isBlocked
    ? 'Push blocked — enable in browser settings (status emails still go to your address on the request)'
    : isOn
      ? 'Push alerts on — click to disable (status emails are always sent)'
      : 'Enable push alerts when status changes (emails are always sent to requesters)';

  return (
    <div className="relative flex-shrink-0">
      {/* Keyframes */}
      {celebrating && (
        <style>{`
          @keyframes spark-out {
            0%   { transform: translate(0,0) scale(1);    opacity: 0.9; }
            60%  { opacity: 0.7; }
            100% { transform: translate(var(--tx),var(--ty)) scale(0); opacity: 0; }
          }
        `}</style>
      )}

      {/* Spark particles */}
      {celebrating && CONFETTI_PARTICLES.map((p, i) => (
        <span
          key={i}
          className="absolute top-1/2 left-1/2 pointer-events-none"
          style={{
            width: p.size,
            height: p.size,
            marginLeft: -p.size / 2,
            marginTop: -p.size / 2,
            borderRadius: p.round ? '50%' : '1px',
            backgroundColor: p.color,
            ['--tx' as string]: p.tx,
            ['--ty' as string]: p.ty,
            animation: `spark-out 0.6s ease-out ${p.delay} forwards`,
          }}
        />
      ))}

      <button
        onClick={isOn ? unsubscribe : subscribe}
        disabled={isLoading || isBlocked}
        title={tooltip}
        className={`relative flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md
          transition-all duration-300
          ${isBlocked ? 'text-subtle cursor-not-allowed' : ''}
          ${isOn && !celebrating ? 'bg-primary/10 text-primary hover:bg-primary/20' : ''}
          ${!isOn && !isBlocked && !isLoading && !celebrating ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' : ''}
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
          ${celebrating ? 'text-white' : ''}
        `}
        style={celebrating ? { backgroundColor: '#FFAA00' } : undefined}
      >
        {isLoading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : isOn ? (
          <Bell className="w-3 h-3" />
        ) : (
          <BellOff className="w-3 h-3" />
        )}
        <span>{isOn ? 'On' : 'Notify me'}</span>
      </button>
    </div>
  );
}

function MyRequestsListInner() {
  const auth = useAuth();
  const [recent, setRecent] = useState<SubmissionRow[]>([]);
  const [searchResults, setSearchResults] = useState<SubmissionRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const token = auth.user?.access_token ?? '';

  const doFetch = async (q?: string): Promise<SubmissionRow[]> => {
    const url = q ? `/api/my-requests?q=${encodeURIComponent(q)}` : '/api/my-requests';
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`${r.status}`);
    const data = await r.json() as SubmissionRow[];
    return Array.isArray(data) ? data : [];
  };

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    doFetch()
      .then(setRecent)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /**
   * Pick up updates that landed while the tab sat open — on refocus, and when the
   * service worker reports an incoming push.
   */
  useEffect(() => {
    if (!token) return;
    let stale = false;
    const refresh = () => {
      if (stale) return;
      doFetch().then((rows) => { if (!stale) setRecent(rows); }).catch(() => { /* keep what we have */ });
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    const onSwMessage = (e: MessageEvent) => {
      if ((e.data as { type?: string } | null)?.type === 'sma-request-update') refresh();
    };

    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => {
      stale = true;
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim()) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      doFetch(search.trim())
        .then(setSearchResults)
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 350);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, token]);

  /** The server marks the request seen when the thread loads — mirror that locally. */
  const clearUnread = useCallback((taskGid: string) => {
    const zero = (rows: SubmissionRow[]) =>
      rows.map((r) => (r.asanaTaskId === taskGid ? { ...r, unreadCount: 0 } : r));
    setRecent(zero);
    setSearchResults(zero);
  }, []);

  const isSearchActive = search.trim().length > 0;
  const displayRows = isSearchActive ? searchResults : recent;
  const isSpinning = loading || (isSearchActive && searching);
  const unreadTotal = recent.reduce((n, r) => n + (r.unreadCount ?? 0), 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <ClipboardList className="w-3.5 h-3.5 text-primary" />
        </div>
        <h2 className="text-base font-semibold text-foreground flex-1">
          My Requests
          {unreadTotal > 0 && (
            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-accent/20 text-foreground align-middle">
              {unreadTotal} new
            </span>
          )}
        </h2>
        <NotificationToggle token={token} />
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title or type…"
          className="w-full h-8 pl-8 pr-7 rounded-md border border-border bg-surface text-[12px] text-foreground
                     placeholder:text-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-interactive focus:border-primary/50 transition"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* States */}
      {isSpinning && (
        <div className="flex items-center justify-center py-10 text-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          <span className="text-sm">{isSearchActive ? 'Searching…' : 'Loading…'}</span>
        </div>
      )}

      {!isSpinning && error && !isSearchActive && (
        <p className="text-sm text-muted text-center py-8">Unable to load your requests.</p>
      )}

      {!isSpinning && !error && displayRows.length === 0 && (
        <p className="text-sm text-muted text-center py-8">
          {isSearchActive ? 'No requests match your search.' : 'No requests submitted yet.'}
        </p>
      )}

      {/* List */}
      {!isSpinning && displayRows.length > 0 && (
        <>
          {isSearchActive && (
            <p className="text-[10px] text-muted mb-2">
              {displayRows.length} result{displayRows.length !== 1 ? 's' : ''}
            </p>
          )}
          <ul className="space-y-2 overflow-y-auto">
            {displayRows.map((row, i) => (
              <RequestRow
                key={row.asanaTaskId || row.id || i}
                row={row}
                token={token}
                onOpened={clearUnread}
              />
            ))}
          </ul>
          <p className="text-[10px] text-muted mt-3 text-center">
            Click a request to see updates and reply
          </p>
        </>
      )}
    </div>
  );
}

export function MyRequestsList() {
  if (!isOidcConfigured()) return null;
  return <MyRequestsListInner />;
}
